import { Container } from 'Container';
import { Image, type ImageProps } from 'Image';
import { useEffect, useState } from 'react';
import { Typography } from 'Typography';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'FrontRoyalConfig';
import { useOfflineModeManager, useTargetBrandConfig } from 'FrontRoyalAngular';
import { InstitutionId } from 'Institutions';
import clsx from 'clsx';

import logoRedQuantic from 'vectors/logos/quantic-logo-rebrand.svg';
import logoBlueValar from 'vectors/footer/logos-valar-logo.svg';
import logoRedSmartly from 'vectors/footer/logos-smartly-logo.svg';
import logoMiyaMiya from 'images/footer/miya-miya-logo.png';
import logoMiyaMiyaRetina from 'images/footer/miya-miya-logo@2x.png';
import availableAppStore from 'vectors/available-app-store.svg';
import availableGooglePlay from 'vectors/available-google-play.svg';
import './locales/footer/footer-en.json';

const logoQuanticImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 155,
        height: 38,
        images: {
            default: logoRedQuantic,
        },
    },
};
const logoValarImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 119,
        height: 32,
        images: {
            default: logoBlueValar,
        },
    },
};
const logoSmartlyImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 137,
        height: 32,
        images: {
            default: logoRedSmartly,
        },
    },
};
const logoMiyaMiyaImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 140,
        height: 32,
        images: {
            default: logoMiyaMiya,
            retina: logoMiyaMiyaRetina,
        },
    },
};
const appStoreImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 94,
        height: 24,
        images: {
            default: availableAppStore,
        },
    },
};
const googlePlayImage: Omit<ImageProps, 'alt'> = {
    mobileImage: {
        width: 100,
        height: 24,
        images: {
            default: availableGooglePlay,
        },
    },
};

const brandLogoMap: Record<InstitutionId, Omit<ImageProps, 'alt'>> = {
    [InstitutionId.quantic]: logoQuanticImage,
    [InstitutionId.valar]: logoValarImage,
    [InstitutionId.smartly]: logoSmartlyImage,
    [InstitutionId.miya_miya]: logoMiyaMiyaImage,
};

export function Footer({ className }: { className?: string }) {
    const { t } = useTranslation('back_royal');
    const config = useConfig();
    const targetBrandConfig = useTargetBrandConfig();
    const showCookiePreferences = config?.gdprAppliesToUser() || false;
    const offlineModeManager = useOfflineModeManager();

    // Hybrid app has no footer.  When offline, none of the
    // links will work, so we hide the whole thing.
    const [showFooter, setShowFooter] = useState(window.CORDOVA ? false : !offlineModeManager.inOfflineMode);
    useEffect(() => {
        setShowFooter(window.CORDOVA ? false : !offlineModeManager.inOfflineMode);
    }, [offlineModeManager]);

    if (!showFooter) {
        // spacer for CORDOVA app since the tab bar overlaps
        return <div className="mt-[55px] sm:mt-0" />;
    }

    return (
        <footer className={clsx('bg-white', className)}>
            <Container className="px-2.5 xs:px-[15px]">
                <div className="flex flex-col items-center gap-6 pb-6 pt-10 md:flex-row md:justify-between">
                    <Link to="/" title={t('footer.footer.homepage') as string}>
                        <Image
                            {...brandLogoMap[targetBrandConfig.institutionId]}
                            className="h-8 flex-shrink-0"
                            alt={targetBrandConfig.brandNameShort}
                        />
                    </Link>
                    <div className="flex gap-6">
                        <Link to="/smartly-ios" target="_blank">
                            <Image
                                {...appStoreImage}
                                className="flex-shrink-0 brightness-100 transition hover:brightness-75"
                                alt={t('footer.footer.available_app_store')}
                            />
                        </Link>
                        <Link to="/smartly-android" target="_blank">
                            <Image
                                {...googlePlayImage}
                                className="flex-shrink-0 brightness-100 transition hover:brightness-75"
                                alt={t('footer.footer.available_google_play')}
                            />
                        </Link>
                    </div>
                </div>
                <hr className="border-slate-grey-200" />
                <div className="pb-10 pt-6 text-center md:flex md:items-center md:justify-center md:pb-8">
                    <Typography variant="link" className="text-slate-grey-400">
                        {t('footer.footer.copyright')}
                        <br className="md:hidden" />
                        <span className="hidden md:inline">&nbsp;</span>
                        <Link
                            to="/privacy"
                            target="_blank"
                            className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                        >
                            {t('footer.footer.privacy_policy')}
                        </Link>
                        &nbsp;|&nbsp;
                        <Link
                            to="/terms"
                            target="_blank"
                            className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                        >
                            {t('footer.footer.terms_and_conditions')}
                        </Link>
                        {showCookiePreferences ? (
                            <>
                                &nbsp;|&nbsp;
                                <Link
                                    to="/#"
                                    className="font-bold text-slate-grey-400 transition hover:text-slate-grey hover:no-underline"
                                    onClick={e => {
                                        e.preventDefault();
                                        // oxlint-disable-next-line new-cap
                                        window.Optanon?.ToggleInfoDisplay();
                                    }}
                                >
                                    {t('footer.footer.cookies')}
                                </Link>
                            </>
                        ) : null}
                    </Typography>
                </div>
            </Container>
        </footer>
    );
}
